<template>
  <div class="base-select-wrapper">
    <label v-if="label" :for="selectId" class="base-select__label">{{ label }}</label>
    <div class="base-select__container">
      <select
        :id="selectId"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        class="base-select"
        @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
        @focus="focused = true"
        @blur="focused = false"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <slot></slot>
      </select>
      <span class="base-select__chevron" :class="{ 'base-select__chevron--focused': focused }">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </div>
    <small v-if="hint" class="base-select__hint">{{ hint }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  modelValue: string | number;
  label?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
}>();

defineEmits<{
  'update:modelValue': [value: string];
}>();

const selectId = computed(() => `select-${Math.random().toString(36).substr(2, 9)}`);
const focused = ref(false);
</script>

<style scoped>
.base-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.base-select__label {
  font-size: 0.7rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.065em;
  transition: color var(--transition-fast);
}

.base-select-wrapper:focus-within .base-select__label {
  color: var(--color-primary);
}

.base-select__container {
  position: relative;
  display: flex;
  align-items: center;
}

.base-select {
  width: 100%;
  height: 40px;
  padding: 0 2.25rem 0 0.875rem;
  background: var(--color-bg-secondary);
  border: 1.5px solid transparent;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  color: var(--color-text-primary);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  outline: none;
}

.base-select:hover:not(:disabled):not(:focus) {
  border-color: var(--color-border-medium);
}

.base-select:focus {
  background: var(--color-bg-primary);
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(30, 86, 217, 0.12);
}

.base-select:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.base-select__chevron {
  position: absolute;
  right: 10px;
  pointer-events: none;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  transition: color var(--transition-fast), transform 0.15s ease;
}

.base-select__chevron--focused {
  color: var(--color-primary);
}

.base-select__hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  margin-top: 2px;
}
</style>
